import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { LayoutDashboardIcon, SparklesIcon, ArrowRightIcon, CameraIcon } from 'lucide-react';
export function ProductsPage() {
  usePageSEO({
    title: 'Products | Design Flow Digitals',
    description: 'Discover premium Shopify themes, apps, and tools by Design Flow Digitals. FlowBoost speed optimization, AI Photoshoot, and custom-built ecommerce solutions.'
  });
  return <div className="pt-32 pb-20" data-id="element-827">
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-828">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-829">Our Products</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-830">
          Premium themes and smart apps built to accelerate your ecommerce
          growth.
        </p>
      </section>

      <div className="flex flex-col gap-16 max-w-6xl mx-auto px-6" data-id="element-831">
        {/* Category Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-832">
          {/* Themes Category */}
          <div className="group glass-card p-10 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 relative overflow-hidden flex flex-col h-full" data-id="element-833">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-[80px] pointer-events-none" data-id="element-834" />

            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-8 border border-[#D4AF37]/20" data-id="element-835">
              <LayoutDashboardIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-836" />
            </div>

            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-837">
              Shopify Themes
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed flex-grow" data-id="element-838">
              Premium Shopify themes designed for modern ecommerce brands. Built
              with clean code and conversion in mind.
            </p>

            <Link to="/themes" className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#D4AF37] transition-colors" data-id="element-839">
              Explore Themes{' '}
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-840" />
            </Link>
          </div>

          {/* Apps Category */}
          <div className="group glass-card p-10 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 relative overflow-hidden flex flex-col h-full" data-id="element-841">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-[80px] pointer-events-none" data-id="element-842" />

            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-8 border border-[#D4AF37]/20" data-id="element-843">
              <SparklesIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-844" />
            </div>

            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-845">Shopify Apps</h2>
            <p className="text-gray-400 mb-8 leading-relaxed flex-grow" data-id="element-846">
              Powerful tools to help merchants grow faster, automate workflows,
              and increase average order value.
            </p>

            <Link to="/apps" className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#D4AF37] transition-colors" data-id="element-847">
              Explore Apps{' '}
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-848" />
            </Link>
          </div>
        </div>

        {/* Featured App Highlight */}
        <div className="mt-12" data-id="element-849">
          <div className="flex items-center gap-4 mb-8" data-id="element-850">
            <h3 className="text-2xl font-bold text-white" data-id="element-851">Featured App</h3>
            <div className="h-px bg-white/10 flex-1" data-id="element-852" />
          </div>

          <div className="glass-card p-8 md:p-12 border-purple-500/30 bg-gradient-to-br from-purple-900/10 to-transparent relative overflow-hidden group" data-id="element-853">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-854" />

            <div className="flex flex-col md:flex-row gap-10 items-center relative z-10" data-id="element-855">
              <div className="w-24 h-24 shrink-0 rounded-2xl bg-purple-500/20 flex items-center justify-center border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.2)]" data-id="element-856">
                <CameraIcon className="w-12 h-12 text-purple-400" data-id="element-857" />
              </div>

              <div className="flex-1 text-center md:text-left" data-id="element-858">
                <div className="flex flex-col md:flex-row items-center gap-3 mb-3" data-id="element-859">
                  <h3 className="text-3xl font-bold text-white" data-id="element-860">
                    AI Product Photoshoot - DFD
                  </h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wider" data-id="element-861">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-862" />{' '}
                    Live
                  </span>
                </div>

                <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-863">
                  Generate AI lifestyle images using your existing store product
                  photos. No studio required.
                </p>

                <Link to="/ai-photoshoot" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white bg-purple-600 hover:bg-purple-500 transition-colors shadow-[0_0_20px_rgba(147,51,234,0.3)]" data-id="element-864">
                  View App Details <ArrowRightIcon className="w-4 h-4" data-id="element-865" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}